/**
 * Course Configuration Store - Local-only settings for course mode personalization
 * Stored in localStorage, not synced to server
 */

import { observable } from '@legendapp/state';
import { logger } from '../lib/logger';

const STORAGE_KEY = 'mellow-course-config';

const DEFAULT_CONFIG = {
  learningStyle: ['Active'],
  communicationStyle: ['Socratic', 'Story Telling'],
  toneStyle: ['Encouraging', 'Humorous', 'Attractive'],
  reasoningFramework: ['Causal'],
};

// Preset options for each category
export const COURSE_PRESETS = {
  learningStyle: [
    'Visual', 'Verbal', 'Active', 'Intuitive',
    'Reflective', 'Global', 'Kinesthetic', 'Sequential',
  ],
  communicationStyle: [
    'Formal', 'Textbook', 'Layman', 'Story Telling', 'Socratic',
    'Interesting', 'Dramatic', 'Attractive', 'Conversational',
    'Concise', 'Detailed', 'Humorous',
  ],
  toneStyle: [
    'Encouraging', 'Neutral', 'Informative', 'Friendly', 'Humorous',
    'Warm', 'Playful', 'Motivating', 'Patient', 'Energetic',
    'Calm', 'Professional', 'Casual',
  ],
  reasoningFramework: [
    'Deductive', 'Inductive', 'Abductive', 'Analogical', 'Causal',
    'Critical', 'Creative', 'Systematic',
  ],
};

// Load from localStorage on init
const loadConfig = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all keys exist
      return { ...DEFAULT_CONFIG, ...parsed };
    }
  } catch (err) {
    logger.error('Failed to load course config', { error: err.message });
  }
  return DEFAULT_CONFIG;
};

// Create observable with initial value from localStorage
export const courseConfig$ = observable(loadConfig());

// Save to localStorage whenever config changes
courseConfig$.onChange(({ value }) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (err) {
    logger.error('Failed to save course config', { error: err.message });
  }
});

/**
 * Update a specific config field
 * @param {string} field - Field name (learningStyle, communicationStyle, etc.)
 * @param {string[]} values - Array of selected values
 */
export const updateCourseConfig = (field, values) => {
  courseConfig$[field].set(values);
};

/**
 * Reset config to defaults
 */
export const resetCourseConfig = () => {
  courseConfig$.set({ ...DEFAULT_CONFIG });
};

/**
 * Get config as object (for API calls)
 */
export const getCourseConfig = () => {
  return courseConfig$.get();
};

/**
 * Get config with temporary override applied (for course suggestions)
 * @param {Object} override - Partial config to merge, e.g. { communicationStyle: ['Dramatic'] }
 * @returns {Object} Merged config
 */
export const getConfigWithOverride = (override) => {
  if (!override) return getCourseConfig();
  return { ...DEFAULT_CONFIG, ...override };
};

/**
 * Parse course suggestion format: "topic - label | configKey=value"
 * @param {string} suggestion - The suggestion string
 * @returns {{ topic: string, label: string, override: Object | null }}
 */
export const parseCourseSuggestion = (suggestion) => {
  // Format: "topic - label | configKey=value"
  const pipeIndex = suggestion.lastIndexOf('|');

  if (pipeIndex === -1) {
    // No override, just topic
    return { topic: suggestion.trim(), label: null, override: null };
  }

  const topicPart = suggestion.substring(0, pipeIndex).trim();
  const configPart = suggestion.substring(pipeIndex + 1).trim();

  // Parse "topic - label"
  const dashIndex = topicPart.lastIndexOf(' - ');
  let topic = topicPart;
  let label = null;

  if (dashIndex !== -1) {
    topic = topicPart.substring(0, dashIndex).trim();
    label = topicPart.substring(dashIndex + 3).trim();
  }

  // Parse "configKey=value"
  const eqIndex = configPart.indexOf('=');
  if (eqIndex === -1) {
    return { topic, label, override: null };
  }

  const key = configPart.substring(0, eqIndex).trim();
  const value = configPart.substring(eqIndex + 1).trim();

  // Validate key is a valid config field
  const validKeys = ['learningStyle', 'communicationStyle', 'toneStyle', 'reasoningFramework'];
  if (!validKeys.includes(key)) {
    return { topic, label, override: null };
  }

  return {
    topic,
    label,
    override: { [key]: [value] }
  };
};

/**
 * Format config values for display: ["A", "B"] → "A and B"
 */
export const formatConfigValue = (arr) => {
  if (!arr || arr.length === 0) return '';
  return arr.join(' and ');
};
